import React, { useState, useRef, useEffect } from "react";
import { Share2, Twitter, Facebook, Linkedin, Instagram, Copy, Check, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Reusable share button.
 *
 * Props:
 *   - text: the message to share (plain text — also used as Twitter content)
 *   - url:  optional canonical URL to attach. Defaults to window.location.origin.
 *   - title: short subject line (used by LinkedIn + Web Share API)
 *   - hashtag: optional hashtag (Twitter)
 *   - testid: data-testid suffix for nested test IDs
 *   - variant: "icon" (compact icon-only) | "button" (full pill with label)
 *   - card: optional { type: "pr" | "goal" | "measurement", payload: object }
 *           — when provided, an extra "Download share card" menu item appears
 *           and the Web Share API will attempt to share the rendered PNG file.
 */
export default function ShareButton({
  text,
  url,
  title = "DAIONE",
  hashtag = "DAIONE",
  testid = "share",
  variant = "icon",
  card,
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const popRef = useRef(null);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.origin : "");
  const fullText = `${text}\n\n${shareUrl}`;

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", onClickAway);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("mousedown", onClickAway);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  /** Fetch the rendered PNG from the backend. Returns a Blob (PNG). */
  const fetchCardBlob = async () => {
    if (!card) return null;
    const { data } = await api.post(
      "/share/card",
      { type: card.type, payload: card.payload || {} },
      { responseType: "blob" }
    );
    return data;
  };

  /** Trigger a browser download of the rendered card. */
  const downloadCard = async () => {
    if (!card) return;
    setDownloading(true);
    try {
      const blob = await fetchCardBlob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `daione-${card.type}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after the download has been kicked off
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      toast.success("Share card downloaded");
    } catch (err) {
      console.error("share card download failed", err);
      toast.error("Could not generate card");
    } finally {
      setDownloading(false);
      setOpen(false);
    }
  };

  const tryNativeShare = async () => {
    if (typeof navigator === "undefined" || !navigator.share) return false;
    // If a card payload is supplied AND the browser supports file-sharing,
    // share the rendered PNG so the user can drop it straight into Instagram, etc.
    if (card && navigator.canShare) {
      try {
        const blob = await fetchCardBlob();
        const file = new File([blob], `daione-${card.type}.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title, text });
          return true;
        }
      } catch {
        /* fall through to text share */
      }
    }
    try {
      await navigator.share({ title, text, url: shareUrl });
      return true;
    } catch {
      return false;
    }
  };

  const handleClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const used = await tryNativeShare();
    if (!used) setOpen((v) => !v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullText);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };

  const openWindow = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=600,height=560");
    setOpen(false);
  };

  const twitter = () =>
    openWindow(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}&hashtags=${encodeURIComponent(hashtag)}`
    );
  const facebook = () =>
    openWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`
    );
  const linkedin = () =>
    openWindow(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(text)}`
    );
  const instagram = async () => {
    // Best path: if a card payload exists, download the PNG first so the user
    // can drop it directly into an Instagram Story.
    if (card) {
      await downloadCard();
      toast.success("Card downloaded — paste into Instagram Story");
    } else {
      await navigator.clipboard.writeText(fullText).catch(() => {});
      toast.success("Caption copied — open Instagram and paste into your Story");
    }
    try {
      window.location.href = "instagram://story-camera";
      setTimeout(() => {
        window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
      }, 800);
    } catch {
      window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    }
    setOpen(false);
  };

  return (
    <div className="relative inline-block" ref={popRef}>
      <button
        onClick={handleClick}
        className={
          variant === "button"
            ? "inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold border border-white/15 hover:border-[#6B9BFF] hover:text-[#6B9BFF] px-3 py-1.5"
            : "w-7 h-7 inline-flex items-center justify-center text-white/40 hover:text-[#6B9BFF] hover:bg-white/5 transition-colors"
        }
        data-testid={`${testid}-trigger`}
        aria-label="Share"
        title="Share"
      >
        <Share2 className={variant === "button" ? "w-3.5 h-3.5" : "w-4 h-4"} />
        {variant === "button" && <span>Share</span>}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 bg-[#121212] border border-white/10 shadow-xl z-50"
          data-testid={`${testid}-menu`}
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
            <div className="label-eyebrow text-white/50 text-[10px]">Share to</div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/40 hover:text-white"
              aria-label="Close"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="divide-y divide-white/5">
            {card && (
              <button
                onClick={downloadCard}
                disabled={downloading}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 hover:text-[#6B9BFF] disabled:opacity-50"
                data-testid={`${testid}-download-card`}
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ImageIcon className="w-4 h-4 text-[#6B9BFF]" />
                )}
                {downloading ? "Generating…" : "Download card (PNG)"}
              </button>
            )}
            <button
              onClick={twitter}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 hover:text-[#6B9BFF]"
              data-testid={`${testid}-twitter`}
            >
              <Twitter className="w-4 h-4" /> Twitter / X
            </button>
            <button
              onClick={facebook}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 hover:text-[#6B9BFF]"
              data-testid={`${testid}-facebook`}
            >
              <Facebook className="w-4 h-4" /> Facebook
            </button>
            <button
              onClick={linkedin}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 hover:text-[#6B9BFF]"
              data-testid={`${testid}-linkedin`}
            >
              <Linkedin className="w-4 h-4" /> LinkedIn
            </button>
            <button
              onClick={instagram}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 hover:text-[#6B9BFF]"
              data-testid={`${testid}-instagram`}
            >
              <Instagram className="w-4 h-4" /> Instagram Story
            </button>
            <button
              onClick={copy}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-white/5 hover:text-[#6B9BFF]"
              data-testid={`${testid}-copy`}
            >
              {copied ? <Check className="w-4 h-4 text-[#6B9BFF]" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy text"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
